import { InputAdornment, styled, Tooltip, Typography } from '@mui/material';
import AppointmentApi, { AppointmentInterface } from 'api/counter/appointment.api';
import ComponentApi from 'api/components-api';
import ClinicApi, { DentistInterface } from 'api/master/clinic.api';
import ModalCustom from 'component/ModalCustom/ModalCustom';
import AutocompleteSelect from 'component/Select/AutocompleteSelect';
import TableCustom, { TablePaginationMiniCustom } from 'component/Table/TableCustom';
import TableRowCommon from 'component/Table/TableRowCommon';
import { PACKAGE_ADDONS } from 'constants/package';
import { CustomFilterDoctor, ListFilterDoctors } from 'features/counter/appointment/head-appointment';
import _, { floor } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { formAppointment } from 'app/slice/appointment.slice';
import { checkPackage, getBranch, numberToCurrencyFormat } from 'utils/app.utils';
import { dateToView } from 'utils/date.utils';
import { APPOINTMENT_HISTORY_DEFAULT_PAGE_LIMIT } from './view-history.utils';

import { faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchIcon from '@mui/icons-material/Search';
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker';
import { icons } from 'constants/images';
import { colors, ThemeIcon } from 'constants/theme';
import { BoxStatus, TextStatus } from 'features/home/UseStyled';
import MainButton from 'new-components/buttons/main-button';
import SelectInput from 'new-components/inputs/select-input';
import TextInput from 'new-components/inputs/text-input';
import { Image } from 'react-bootstrap';
export interface IViewHistoryProps {
  onClose: () => void
  onEdit?: (appointmentId: number, isEditSpecialInfo: boolean, date: string, branchId: number) => void
}

/** STYLE CUSTOM */
const TableConfig = styled('div')(({ theme }) => ({
  td: { height: '48px' },
  '.MuiTableBody-root': {
    '.MuiTableCell-root': {
      verticalAlign: 'top !important',
      paddingBottom: '12px !important'
    }
  },
  '& .appointmentOperatives': { maxWidth: '200px' },
  '& .practice': { minWidth: '300px', maxWidth: '400px' },
  '& .remark': { minWidth: '300px', maxWidth: '400px' },
  '.MuiPaper-root': {
    maxHeight: "calc(100vh - 230px)",
    overflow: "auto"
  },
  // '.MuiPaper-root::-webkit-scrollbar': {
  //   width: '0px',
  //   height: '0px',
  // },
  '.MuiPaper-root table thead': {
    backgroundColor: `${colors.themeMainColor} !important`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  '.MuiPaper-root table thead tr th': {
    position: 'sticky',
    top: 0,
    zIndex: 1,
  }
}))

const THead = styled('div')(({ theme }) => ({
  display: 'flex',
  backgroundColor: colors.themeMainColor,
  borderRadius: '8px 8px 0 0',
  padding: '0.5rem 1.5rem',
  fontSize: 16,
  color: colors.white
}))

const TBody = styled('div')(({ theme }) => ({
  border: `1px solid ${colors.lightGray}`,
  borderTopWidth: 0,
  color: colors.textPrimary,
  fontSize: 16,
  padding: '0.625rem 1.5rem',
  '.expand-more': {
    backgroundColor: colors.white,
    border: `1px solid ${colors.textLightGray}`,
    color: colors.themeMainColor,
    borderRadius: 100,
    fontSize: 22
  }
}))

const Cell = styled('div')(({ theme }) => ({ '&:first-of-type': { marginRight: '1.25rem', width: '100%' } }))

export default function ViewHistory(props: IViewHistoryProps) {
  const { t } = useTranslation()

  /** Branch input-visibility settings — same source the booking form uses to decide which inputs to render */
  const formAppointments: any[] = useSelector(formAppointment) || []
  const isFieldShown = (id?: string | null) => {
    if (!id) return true
    if (!formAppointments?.length) return true
    const setting = _.find(formAppointments, { id })
    return setting ? _.get(setting, 'isShow') !== false : true
  }

  const [doctors, setDoctors] = useState<DentistInterface[]>([])
  const [appointments, setAppointments] = useState<AppointmentInterface[]>([])
  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(APPOINTMENT_HISTORY_DEFAULT_PAGE_LIMIT)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('date')
  const [sortType, setSortType] = useState('DESC')
  const [status, setStatus] = useState<boolean>(true)

  /** FILTER */
  const [search, setSearch] = useState('')
  const [doctor, setDoctor] = useState<number>(0)
  const [rangeDate, setRangeDate] = useState<string[]>(emptyRangePicker)

  const [fetch, setFetch] = useState<boolean>(false);
  const [conditionLoad, setConditionLoad] = useState<any>({})

  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  /** TDH customization — appointment channel lookup */
  const [appointmentChannels, setAppointmentChannels] = useState<Array<{ appointmentChannelId: number; channelName: string }>>([])
  /** O(1) channel-id → name lookup ใช้ใน renderData แทน _.find ที่เป็น O(N×M) */
  const appointmentChannelMap = useMemo(
    () => new Map(appointmentChannels.map((c) => [c.appointmentChannelId, c.channelName])),
    [appointmentChannels]
  )

  /** VARIABLE — memo so the 14 status entries (incl. JSX icons) don't recreate per render */
  const statusIcon: any = useMemo(() => ({
    NORMAL: { icon: <FontAwesomeIcon style={{ color: colors.white }} icon={faUser} />, color: colors.appointmentStatus.statusNormal, label: t('APPOINTMENT.STATUS.NORMAL'), disabled: false },
    CANCEL_APP: { icon: <Image style={{ height: '15px' }} src={icons.appointment.cancel_app} />, color: colors.appointmentStatus.statusCancel, label: t('APPOINTMENT.STATUS.CANCEL'), disabled: false },
    CANCEL: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconCancel} />, color: colors.appointmentStatus.statusCancel, label: t('APPOINTMENT.STATUS.CANCEL'), disabled: false },
    CAN_NOT_CONTACT: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconNotConnect} />, color: colors.appointmentStatus.statusUnConnect, label: t('APPOINTMENT.STATUS.CAN_NOT_CONTACT'), disabled: false },
    PUT_OFF: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconChangeSchedule} />, color: colors.appointmentStatus.statusChange, label: t('APPOINTMENT.STATUS.PUT_OFF'), disabled: false },
    CONFIRM: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconEnter} />, color: colors.appointmentStatus.statusEnter, label: t('APPOINTMENT.STATUS.CONFIRM'), disabled: false },
    ARRIVED: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconUserCheckTrue} />, color: colors.appointmentStatus.statusArrived, label: t('APPOINTMENT.STATUS.ARRIVED'), disabled: false },
    NOT_YET: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconUserCheckFalse} />, color: colors.appointmentStatus.statusUnArrived, label: t('APPOINTMENT.STATUS.NOT_YET'), disabled: false },
    PAYMENT: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconPendingPayment} />, color: colors.appointmentStatus.statusWaitPayment, label: t('APPOINTMENT.STATUS.PENDING_PAYMENT'), disabled: true },
    PENDING_PAYMENT: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconWaitPayment} />, color: colors.appointmentStatus.statusPayment, label: t('APPOINTMENT.STATUS.PAYMENT'), disabled: true },
    PENDING_SERVICE: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconCounterCheckIn} />, color: colors.appointmentStatus.statusCounterCheckIn, label: t('APPOINTMENT.STATUS.PENDING_SERVICE'), disabled: true },
    SERVICE: { icon: <Image style={{ height: '17px' }} src={icons.appointment.iconService} />, color: colors.appointmentStatus.statusService, label: t('APPOINTMENT.STATUS.SERVICE'), disabled: true },
    DONE: { icon: <Image style={{ height: '15px' }} src={ThemeIcon.DoneTreatment} />, color: colors.appointmentStatus.statusDone, label: t('APPOINTMENT.STATUS.DONE'), disabled: true },
  }), [t])

  const handleDateSend = (date: any, num: number) => {
    if (!date) {
      return moment().add(num, 'months').format('YYYY-MM-DD')
    }
    return date
  }

  const handleConditionLoad = (dataCondition: any, statusPage?: boolean) => {
    if (statusPage) {
      /** Pagination: ใช้ filter ที่ user กดค้นหาล่าสุด — ถ้ายังไม่เคยกดค้นหา (conditionLoad ว่าง)
       *  ให้ใช้ค่า filter ปัจจุบันแทน (ป้องกัน BE คืน "ทุกข้อมูล" ตอน mount ครั้งแรก) */
      return Object.keys(conditionLoad).length > 0
        ? { ...conditionLoad, page, pageLimit }
        : dataCondition
    }

    if (fetch || status) {
      return dataCondition
    } else {
      return conditionLoad
    }
  }

  const loadDataAppointment = async (statusPage?: boolean) => {
    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (search) condition = { ...condition, search: search }
    if (doctor) condition = { ...condition, doctorId: doctor }
    if (rangeDate?.[0] && rangeDate?.[1]) condition = { ...condition, dateStart: handleDateSend(rangeDate[0], -3), dateEnd: handleDateSend(rangeDate[1], 3) }
    if (branchId) condition = { ...condition, branchId: branchId === "ALL" ? '' : branchId }
    await AppointmentApi.findAll(handleConditionLoad(condition, statusPage)).then(({ data, headers }) => {
      if (data) {
        const newData: any = data.sort((a: any, b: any) => {
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        });
        setRowCount(headers['x-total'])
        setAppointments(newData)
        if (fetch || status) {
          setConditionLoad(condition)
        }
        setStatus(false)
      }
    }).finally(() => {
      setFetch(false)
    })
  }

  /** เรียก loadDataAppointment เฉพาะเมื่อ fetch ถูก toggle เป็น true จากปุ่มค้นหา
   *  (skip ตอน mount ซึ่ง fetch=false initial และ skip ตอน finally reset เป็น false)
   *  — useEffect ตัวถัดไปดูแลการ load ตอน mount + เปลี่ยน page/pageLimit */
  useEffect(() => {
    if (!fetch) return
    loadDataAppointment()
  }, [fetch])

  useEffect(() => {
    loadDataAppointment(true)
  }, [page, pageLimit])

  const searchCilck = (() => {
    setPage(1)
    setFetch(true);
  })

  /** Load lookups (dentists / branches / channels) in parallel — was 3 sequential calls */
  useEffect(() => {
    Promise.all([
      ClinicApi.findAllDentists(),
      ClinicApi.findAllBranches(),
      ComponentApi.findAllAppointmentChannels()
    ]).then(([dentistsRes, branchesRes, channelsRes]: any[]) => {
      if (dentistsRes?.data) setDoctors(dentistsRes.data)
      if (branchesRes?.data) setBranches(_.map(branchesRes.data, (item: any) => ({ value: item.branchId, name: item.branchName || item.branchNameEn })))
      if (channelsRes?.status === 200) setAppointmentChannels(channelsRes.data || [])
    }).catch(() => { /* lookups are non-critical; table still renders */ })
  }, [])


  /** headCells with `settingId` for customizable ones — filtered against branch isShow */
  const headCells = useMemo(() => {
    const all: any[] = [
      { id: 'no', disablePadding: false, label: t('#'), class: 'text-nowrap' },
      { id: 'branchId', disablePadding: false, label: t('APPOINTMENT.HISTORY.BRANCH'), class: 'text-nowrap', settingId: 'branch' },
      { id: 'date', disablePadding: false, label: t('APPOINTMENT.HISTORY.APPOINTMENT_DATE'), class: 'text-nowrap', settingId: 'date' },
      { id: 'timeStart', disablePadding: false, label: t('APPOINTMENT.HISTORY.TIME_START'), class: 'text-nowrap', settingId: 'timeStart' },
      { id: 'durationMinute', disablePadding: false, label: t('APPOINTMENT.HISTORY.APPOINTMENT_DURATION'), class: 'text-nowrap', settingId: 'duration' },
      { id: 'cnNumber', disablePadding: false, label: t('APPOINTMENT.HISTORY.CN_NUMBER'), class: 'text-nowrap' },
      { id: 'branchCnNumber', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.BRANCH_ID'), class: 'text-nowrap' },
      { id: 'patientFullname', disablePadding: false, label: t('APPOINTMENT.HISTORY.PATIENT_NAME'), class: 'text-nowrap' },
      { id: 'phone', disablePadding: false, label: t('APPOINTMENT.HISTORY.PHONE'), class: 'text-nowrap', settingId: 'phone' },
      { id: 'doctorFullname', disablePadding: false, label: t('APPOINTMENT.HISTORY.DOCTOR'), class: 'text-nowrap', settingId: 'doctor' },
      { id: 'appointmentOperatives', disablePadding: false, label: t('APPOINTMENT.HISTORY.SERVICE'), class: 'text-nowrap ', style: { maxHeight: '30px' }, settingId: 'treatment' },
      { id: 'statusAppointment', disablePadding: false, label: t('APPOINTMENT.HISTORY.STATUS'), class: 'text-nowrap' },
      { id: 'price', disablePadding: false, label: t('APPOINTMENT.HISTORY.PRICE'), class: 'text-nowrap', settingId: 'price' },
      { id: 'hasLab', disablePadding: false, label: t('APPOINTMENT.HISTORY.LAB'), class: 'text-nowrap', settingId: 'lab' },
      { id: 'hasXray', disablePadding: false, label: t('APPOINTMENT.HISTORY.XRAY'), class: 'text-nowrap', settingId: 'xray' },
      { id: 'chiefComplaint', disablePadding: false, label: t('APPOINTMENT.FORM.CHIEF_COMPLAINT'), class: 'text-nowrap', style: { width: 350, minWidth: 350, maxWidth: 350 }, settingId: 'chiefComplaint' },
      /** 'การปฏิบัติ' column removed; practice field now holds Special Request data */
      { id: 'specialRequest', disablePadding: false, label: t('APPOINTMENT.FORM.SPECIAL_REQUEST'), class: 'text-nowrap', style: { width: 350, minWidth: 350, maxWidth: 350 }, settingId: 'specialRequest' },
      { id: 'appointmentChannelId', disablePadding: false, label: t('APPOINTMENT.FORM.APPOINTMENT_CHANNEL'), class: 'text-nowrap', settingId: 'appointmentChannelId' },
      { id: 'feedback', disablePadding: false, label: t('APPOINTMENT.FORM.FEEDBACK'), class: 'text-nowrap', style: { width: 350, minWidth: 350, maxWidth: 350 }, settingId: 'feedback' },
      { id: 'remark', disablePadding: false, label: t('APPOINTMENT.HISTORY.REMARK'), class: 'text-nowrap', style: { width: 350, minWidth: 350, maxWidth: 350 }, settingId: 'note' },
      { id: 'createdBy', align: "center", disablePadding: false, label: t('APPOINTMENT.HISTORY.CREATED_NEW'), class: 'text-nowrap' },
      { id: 'updatedBy', align: "center", disablePadding: false, label: t('APPOINTMENT.HISTORY.UPDATED_NEW'), class: 'text-nowrap' },
      { id: 'action', disablePadding: false, label: t('APPOINTMENT.HISTORY.ACTION') },
    ]
    return all.filter((c) => isFieldShown(c.settingId))
  }, [t, formAppointments])

  const getDurationTextByTimeMinute = (minute: number) => {
    if (minute >= 60) return `${floor(minute / 60)} ${t('TIME_DURATION_TYPE.HOURS')} ` + (minute % 60 > 0 ? `${minute % 60} ${t('TIME_DURATION_TYPE.MINUTES')}` : '')
    else return `${minute} ${t('TIME_DURATION_TYPE.MINUTES')}`
  }

  const renderDataSlim = (objData: AppointmentInterface, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { appointmentId, branchName, date, timeStart, durationMinute, cnNumber, branchCnNumber, patientFullname, doctorFullname, appointmentOperatives, statusAppointment, price, practice, remark, hasLab } = objData
    const isEditSpecialInfo = statusAppointment === "DONE" ? true : false
    return (
      <TBody>
        <div className={'d-flex align-items-center'}>
          <Cell>
            <Row className="pl-4 mx-0">
              <div className="position-absolute w-auto px-0" style={{ marginLeft: '-1.5rem' }}>
                {no}
              </div>
              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.BRANCH')}
              </Col>
              <Col xs={6} sm={3}>
                {branchName}
              </Col>
              <Col xs={6} sm={4} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.APPOINTMENT_DATE')}
              </Col>
              <Col xs={6} sm={2}>
                {dateToView(date)}
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.TIME_START')}
              </Col>
              <Col xs={6} sm={3}>{`${timeStart.replace(':00', '').replace(':', '.')} ${t('TIME_UNIT')}`}</Col>
              <Col xs={6} sm={4} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.APPOINTMENT_DURATION')}
              </Col>
              <Col xs={6} sm={2}>
                {getDurationTextByTimeMinute(durationMinute)}
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.CN_NUMBER')}
              </Col>
              <Col xs={6} sm={3}>
                {cnNumber || '-'}
              </Col>
              <Col xs={6} sm={4} className="font-weight-bold">
                {t('PATIENT_MANAGEMENT.TABLE.CELL.BRANCH_ID')}
              </Col>
              <Col xs={6} sm={2}>
                {branchCnNumber || '-'}
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.PATIENT_NAME')}
              </Col>
              <Col xs={6} sm={9}>
                {patientFullname || '-'}
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.DOCTOR')}
              </Col>
              <Col xs={6} sm={9}>
                {doctorFullname || '-'}
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.SERVICE')}
              </Col>
              <Col xs={6} sm={9}>

                <Tooltip title={String(_.map(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName).join(', '))} placement="left">
                  <div>{String(_.map(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName).join(', '))}</div>
                </Tooltip>
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.STATUS')}
              </Col>
              <Col xs={6} sm={3}>
                {renderAppointStatus(statusAppointment)}
              </Col>
              <Col xs={6} sm={4} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.PRICE')}
              </Col>
              <Col xs={6} sm={2}>
                {numberToCurrencyFormat(price)}
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {/** label renamed to 'Special Request' — practice column now stores SR data */}
                {t('APPOINTMENT.FORM.SPECIAL_REQUEST')}
              </Col>
              <Col xs={6} sm={9} className={'hard-break'}>
                {practice || '-'}
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.REMARK')}
              </Col>
              <Col xs={6} sm={9} className={'hard-break'}>
                {remark || '-'}
              </Col>
              {checkPackage(PACKAGE_ADDONS.ADDON_LAB) && (
                <>
                  <Col xs={6} sm={3} className="font-weight-bold">
                    {t('APPOINTMENT.HISTORY.LAB')}
                  </Col>
                  <Col xs={6} sm={3} className="-font-weight-bold">
                    {t(`${hasLab}`)}
                  </Col>
                </>
              )}
              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.ACTION')}
              </Col>
              <Col xs={6} sm={3} className="-font-weight-bold">
                <img src={icons.dropdownEdit} alt="dropdownEdit" className="align-baseline cursor-pointer mx-1" onClick={() => props.onEdit && props.onEdit(appointmentId, isEditSpecialInfo, date, branchId)} />
              </Col>
            </Row>
          </Cell>
        </div>
      </TBody>
    )
  }

  const renderAppointStatus = (status: string) => {
    return <BoxStatus style={{ marginLeft: '5px' }} bg={statusIcon[status]?.color || ''}>
      {statusIcon[status]?.icon || ''}
      <TextStatus  >
        {statusIcon[status]?.label || ''}
      </TextStatus>
    </BoxStatus>
  }

  const renderData = (objData: AppointmentInterface, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { appointmentId, branchName, date, timeStart, durationMinute, cnNumber,
      branchCnNumber, patientFullname, phone, doctorFullname, appointmentOperatives,
      statusAppointment, price, practice, chiefComplaint, appointmentChannelId, feedback, remark, hasLab, hasXray, createdBy, createdAt, updatedBy, updatedAt } = objData as any
    const isEditSpecialInfo = statusAppointment === "DONE" ? true : false
    /** dedupe: คำนวณ service label ครั้งเดียวแทนซ้ำใน Tooltip title + content */
    const serviceLabel = String(_.map(_.filter(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName), (op: any) => op.operativeName || op.operativeTypeName).join(', '))
    const objRenderData = {
      key: String(appointmentId),
      id: String(appointmentId),
      obj: objData,
      /** Conditional spread keeps columns in lockstep with headCells filter */
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        ...(isFieldShown('branch') ? [{ option: 'TEXT', align: 'center', label: branchName || '', class: 'text-nowrap-ellipsis' }] : []),
        ...(isFieldShown('date') ? [{ option: 'TEXT', align: 'left', label: dateToView(date), class: 'text-nowrap-ellipsis' }] : []),
        ...(isFieldShown('timeStart') ? [{ option: 'TEXT', align: 'center', label: `${timeStart.replace(':00', '').replace(':', '.')} น.`, class: 'text-nowrap-ellipsis' }] : []),
        ...(isFieldShown('duration') ? [{ option: 'TEXT', align: 'center', label: getDurationTextByTimeMinute(durationMinute) }] : []),
        { option: 'TEXT', align: 'left', label: cnNumber || '-' },
        { option: 'TEXT', align: 'left', label: branchCnNumber || '-' },
        { option: 'TEXT', align: 'left', label: patientFullname, class: 'text-nowrap' },
        ...(isFieldShown('phone') ? [{ option: 'TEXT', align: 'left', label: phone, class: 'text-nowrap' }] : []),
        ...(isFieldShown('doctor') ? [{ option: 'TEXT', align: 'left', label: doctorFullname, class: 'text-nowrap' }] : []),
        ...(isFieldShown('treatment') ? [{
          option: 'COMPONENT', align: 'left', component: (<div>
            <Tooltip title={serviceLabel} placement='bottom'>
              <div>{serviceLabel}</div>
            </Tooltip>
          </div>)
          , class: 'text-nowrap-ellipsis appointmentOperatives'
        }] : []),
        { option: 'TEXT', align: 'left', label: renderAppointStatus(statusAppointment), class: 'text-nowrap-ellipsis' },
        ...(isFieldShown('price') ? [{ option: 'TEXT', align: 'right', label: numberToCurrencyFormat(price) }] : []),
        ...(isFieldShown('lab') ? [{ option: 'TEXT', align: 'left', label: hasLab === "NOT_HAS" ? t('APPOINTMENT.NOT_HAS') : t('APPOINTMENT.HAS'), class: 'text-nowrap-ellipsis' }] : []),
        ...(isFieldShown('xray') ? [{ option: 'TEXT', align: 'left', label: hasXray === "NOT_HAS" ? t('APPOINTMENT.NOT_HAS') : t('APPOINTMENT.HAS'), class: 'text-nowrap-ellipsis' }] : []),
        ...(isFieldShown('chiefComplaint') ? [{ option: 'TEXT', align: 'left', label: chiefComplaint || '-', class: 'hard-break', style: { width: 350, minWidth: 350, maxWidth: 350, whiteSpace: 'normal', wordBreak: 'break-word' } }] : []),
        /** Special Request column reads from the merged practice field */
        ...(isFieldShown('specialRequest') ? [{ option: 'TEXT', align: 'left', label: practice || '-', class: 'hard-break', style: { width: 350, minWidth: 350, maxWidth: 350, whiteSpace: 'normal', wordBreak: 'break-word' } }] : []),
        ...(isFieldShown('appointmentChannelId') ? [{ option: 'TEXT', align: 'left', label: appointmentChannelMap.get(appointmentChannelId) || '-', class: 'text-nowrap' }] : []),
        ...(isFieldShown('feedback') ? [{ option: 'TEXT', align: 'left', label: feedback || '-', class: 'hard-break', style: { width: 350, minWidth: 350, maxWidth: 350, whiteSpace: 'normal', wordBreak: 'break-word' } }] : []),
        ...(isFieldShown('note') ? [{ option: 'TEXT', align: 'left', label: remark, class: '-text-nowrap-ellipsis remark hard-break', style: { width: 350, minWidth: 350, maxWidth: 350, whiteSpace: 'normal', wordBreak: 'break-word' } }] : []),
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: createdBy, updatedAt: createdAt }, class: 'text-nowrap-ellipsis' },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt }, class: 'text-nowrap-ellipsis' },
        // checkPackage(PACKAGE_ADDONS.ADDON_LAB) && { option: 'TEXT', align: 'center', label: t(`${hasLab}`) },
        {
          option: 'COMPONENT', align: 'center', component:
            <img src={icons.dropdownEdit} alt="dropdownEdit" className="align-baseline cursor-pointer mx-1" onClick={() => props.onEdit && props.onEdit(appointmentId, isEditSpecialInfo, date, branchId)} />

        }
      ]
    }
    return <TableRowCommon {...objRenderData} />
  }

  return <ModalCustom
    size={'xl'}
    title={t('APPOINTMENT.HISTORY.TITLE')}
    alignFooter={'end'}
    fullscreen
    closeButton
    onClose={props.onClose}
    component={
      <>
        <Row className="mb-2 mx-0 mt-2">
          <Col xs={12} md={12} xl={3} className="px-1 mb-2">
            <TextInput
              label={t('APPOINTMENT.FORM.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              fullWidth
              inputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon /></InputAdornment>) }}
            />
          </Col>
          <Col xs={12} md={12} xl={3}>
            <SelectInput
              onChange={(e) => {
                setBranchId(e.target.value)
                setPage(1)
              }}
              options={[{ value: 'ALL', name: t('STATUS.ALL') }, ...branches]}
              label={t('REPORT.FILTER.BRANCH')}
              value={branchId}

            />
          </Col>
          <Col xs={12} md={6} xl={3} className="px-1 mb-2">
            <CustomFilterDoctor className='position-relative' >
              <Typography className='position-absolute edit-filter-doctor'>{t('USER_DOCTOR')}:</Typography>
              <AutocompleteSelect
                labelId="select-dentist"
                options={doctors}
                getOptionLabel={(option) => option.dentistFullname || t('ALL')}
                renderOption={(props, option) => {
                  return (
                    <ListFilterDoctors {...props} key={option.dentistId} component="li">
                      {option.dentistFullname}
                    </ListFilterDoctors>
                  )
                }}
                onchange={(e, value) => setDoctor(value?.dentistId)}
                noOptionsText={t('NOT_FOUND')}
                filterOptions={(option) => option.dentistFullname}
                value={_.find(doctors, { dentistId: doctor }) || ''}
                height={40}
                disableClearable={!doctor}
              />
            </CustomFilterDoctor>
          </Col>
          <Col xs={12} md={6} xl={2} className="px-1 mb-2">
            <InputSecondNewRangePicker
              value={rangeDate}
              inputHeight={40}
              allowClear
              onClear={() => {
                setRangeDate(emptyRangePicker)
                setPage(1)
              }}
              onchange={(val: any) => {
                /** picker fire onchange ระหว่างเลือก (เช่น [Date, ''] หลังคลิกวันแรก)
                 *  — รออัปเดต state จนกว่าทั้ง 2 วันจะถูกเลือกครบ
                 *  — ล้างทั้งหมด (clear button หรือลบทั้งคู่) → ปล่อยเป็นค่าว่าง
                 *    (condition build ที่ ~line 190 จะข้าม dateStart/dateEnd เมื่อ rangeDate ว่าง) */
                if (val?.[0] && val?.[1]) {
                  setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                  setPage(1)
                } else if (_.isEmpty(val) || (!val?.[0] && !val?.[1])) {
                  setRangeDate(emptyRangePicker)
                  setPage(1)
                }
              }}
            />
          </Col>
          <Col xs={12} md={6} xl={1} className="px-1 mb-2">
            <MainButton onClick={() => searchCilck()} title={t('INPUT.SEARCH')} type='primary' variant='contained' />
          </Col>
        </Row>
        <div className="d-none d-xl-block">
          <TableConfig>
            <TableCustom
              customScroll
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              rowCount={rowCount}
              onSort={(sortByVal: string, sortTypeVal: string) => {
                setSortType(sortTypeVal)
                setSortBy(sortByVal)
              }}
              setPageLimit={setPageLimit}
              setPage={setPage}
              headCells={headCells}
              rowsData={_.map(appointments, (val, i) => renderData(val, i))}
              tableMinWidth={2800}
            />
          </TableConfig>
        </div>
        <div className="d-block d-xl-none">
          <THead>
            <Cell>{t('#')}</Cell>
          </THead>
          <div className={'treatment-body'}>
            {_.map(appointments, renderDataSlim)}
            <div className={'mt-2'}>
              <TablePaginationMiniCustom
                count={rowCount || 0}
                page={page}
                rowsPerPage={pageLimit}
                rowCount={rowCount}
                pageLimit={pageLimit}
                headCells={headCells}
                onPageChange={(event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage)}
                setPageLimit={(event: any) => {
                  setPageLimit(parseInt(event.target.value, 10))
                  setPage(1)
                }}
                setPage={setPage}
              />
            </div>
          </div>
        </div>
      </>
    }
  />
}
